import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Table } from "reactstrap";
import Avatar from "../Avatar";
import {
  LinkedInBusinessAnalyticsSnapshot,
  LinkedInDemographicRow,
  LinkedInEngagementDaily,
  LinkedInKpiMetric,
  LinkedInTrendDir,
} from "./linkedinBusinessAnalyticsTypes";
import "./LinkedInBusinessAnalytics.scss";

const LINKEDIN_BLUE = "#0077B5";

interface Props {
  snapshot: LinkedInBusinessAnalyticsSnapshot;
}

const formatNum = (n: number, decimals = 0) =>
  new Intl.NumberFormat("es-CL", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(n);

function deltaClass(dir?: LinkedInTrendDir) {
  if (dir === "up") return "pos";
  if (dir === "down") return "neg";
  if (dir === "flat") return "flat";
  return null;
}

function KpiCard({ metric, tone }: { metric: LinkedInKpiMetric; tone: string }) {
  const intl = useIntl();
  const delta = deltaClass(metric.direction);
  return (
    <div className={cn("li-analytics-kpi", `li-analytics-kpi--${tone}`)}>
      <div className="li-analytics-kpi__value">
        {formatNum(metric.value, metric.decimals ?? 0)}
      </div>
      <div className="li-analytics-kpi__label">
        {intl.formatMessage({ id: metric.labelKey })}
      </div>
      {delta ? (
        <span className={cn("li-analytics-kpi__delta", `li-analytics-kpi__delta--${delta}`)}>
          <FontAwesomeIcon
            icon={
              metric.direction === "down"
                ? ["fas", "arrow-down"]
                : metric.direction === "flat"
                ? ["fas", "minus"]
                : ["fas", "arrow-up"]
            }
          />
        </span>
      ) : null}
    </div>
  );
}

function MiniBarChart({
  labels,
  values,
  color = LINKEDIN_BLUE,
}: {
  labels: string[];
  values: number[];
  color?: string;
}) {
  const max = Math.max(1, ...values);
  return (
    <div className="li-analytics-bars" aria-hidden>
      {values.map((v, i) => (
        <div key={labels[i] || i} className="li-analytics-bars__col">
          <div
            className="li-analytics-bars__bar"
            style={{ height: `${Math.max(4, (v / max) * 100)}%`, backgroundColor: color }}
            title={`${labels[i]}: ${formatNum(v)}`}
          />
          <span className="li-analytics-bars__label">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

function DemographicList({ rows, emptyKey }: { rows: LinkedInDemographicRow[]; emptyKey: string }) {
  const intl = useIntl();
  if (!rows.length) {
    return (
      <p className="li-analytics-muted mb-0">{intl.formatMessage({ id: emptyKey })}</p>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="li-analytics-demo list-unstyled mb-0">
      {rows.map((row) => (
        <li key={row.label} className="li-analytics-demo__row">
          <span className="li-analytics-demo__label">{row.label}</span>
          <div className="li-analytics-demo__track">
            <div
              className="li-analytics-demo__fill"
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </div>
          <span className="li-analytics-demo__count">{formatNum(row.count)}</span>
        </li>
      ))}
    </ul>
  );
}

function engagementTotals(days: LinkedInEngagementDaily[]) {
  return days.reduce(
    (acc, d) => ({
      impressions: acc.impressions + d.impressions,
      clicks: acc.clicks + d.clicks,
      likes: acc.likes + d.likes,
      comments: acc.comments + d.comments,
      shares: acc.shares + d.shares,
    }),
    { impressions: 0, clicks: 0, likes: 0, comments: 0, shares: 0 }
  );
}

export function LinkedInBusinessAnalyticsDashboard({ snapshot }: Props) {
  const intl = useIntl();
  const [tab, setTab] = useState<"community" | "content" | "demographics">("community");

  const pageViewLabels = useMemo(
    () => snapshot.pageViewsDaily.map((d) => d.label),
    [snapshot.pageViewsDaily]
  );
  const pageViewValues = useMemo(
    () => snapshot.pageViewsDaily.map((d) => d.pageViews),
    [snapshot.pageViewsDaily]
  );
  const followerLabels = useMemo(
    () => snapshot.followersDaily.map((d) => d.label),
    [snapshot.followersDaily]
  );
  const followerNetValues = useMemo(
    () => snapshot.followersDaily.map((d) => d.netFollowers),
    [snapshot.followersDaily]
  );
  const engagementTotalsPeriod = useMemo(
    () => engagementTotals(snapshot.engagementDaily),
    [snapshot.engagementDaily]
  );

  return (
    <div className="li-analytics-root">
      <div className="li-analytics-banner">
        {intl.formatMessage({ id: snapshot.meta.dataLagNoteKey })}
      </div>

      <div className="li-analytics-org">
        {snapshot.organizationAvatarUrl ? (
          <Avatar image={snapshot.organizationAvatarUrl} width="48px" height="48px" />
        ) : (
          <div className="li-analytics-org__icon" style={{ color: LINKEDIN_BLUE }}>
            <FontAwesomeIcon icon={["fab", "linkedin"]} size="2x" />
          </div>
        )}
        <div>
          <h2 className="li-analytics-org__name">{snapshot.organizationName}</h2>
          <p className="li-analytics-muted mb-0">
            {snapshot.periodSinceLabel} — {snapshot.periodUntilLabel}
          </p>
        </div>
      </div>

      <div className="li-analytics-tabs mb-3">
        {(["community", "content", "demographics"] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={cn("li-analytics-tab", tab === key && "li-analytics-tab--active")}
            onClick={() => setTab(key)}
          >
            {intl.formatMessage({ id: `app.linkedinBusinessAnalytics.tab.${key}` })}
          </button>
        ))}
      </div>

      {tab === "community" ? (
        <>
          <div className="li-analytics-kpi-grid">
            <KpiCard metric={snapshot.overview.followers} tone="followers" />
            <KpiCard metric={snapshot.overview.newFollowers} tone="growth" />
            <KpiCard metric={snapshot.overview.pageViews} tone="views" />
            <KpiCard metric={snapshot.overview.impressions} tone="impressions" />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.section.followersDaily" })}</h3>
            <MiniBarChart labels={followerLabels} values={followerNetValues} />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.section.pageViewsDaily" })}</h3>
            <MiniBarChart labels={pageViewLabels} values={pageViewValues} color="#004182" />
          </div>
        </>
      ) : null}

      {tab === "content" ? (
        <>
          <div className="li-analytics-kpi-grid li-analytics-kpi-grid--wide">
            <KpiCard metric={snapshot.overview.impressions} tone="impressions" />
            <KpiCard metric={snapshot.overview.engagement} tone="engagement" />
            <KpiCard metric={snapshot.overview.clicks} tone="clicks" />
            <KpiCard metric={snapshot.overview.likes} tone="likes" />
            <KpiCard metric={snapshot.overview.comments} tone="comments" />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.section.contentSummary" })}</h3>
            <div className="li-analytics-summary-row">
              <span>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.impressions" })}</span>
              <strong>{formatNum(engagementTotalsPeriod.impressions)}</strong>
            </div>
            <div className="li-analytics-summary-row">
              <span>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.clicks" })}</span>
              <strong>{formatNum(engagementTotalsPeriod.clicks)}</strong>
            </div>
            <div className="li-analytics-summary-row">
              <span>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.likes" })}</span>
              <strong>{formatNum(engagementTotalsPeriod.likes)}</strong>
            </div>
            <div className="li-analytics-summary-row">
              <span>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.comments" })}</span>
              <strong>{formatNum(engagementTotalsPeriod.comments)}</strong>
            </div>
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.section.topPosts" })}</h3>
            {snapshot.postsTable.length === 0 ? (
              <p className="li-analytics-muted mb-0">
                {intl.formatMessage({ id: "app.linkedinBusinessAnalytics.noPosts" })}
              </p>
            ) : (
              <div className="table-responsive">
                <Table className="li-analytics-table mb-0" size="sm" hover>
                  <thead>
                    <tr>
                      <th>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.col.post" })}</th>
                      <th>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.col.date" })}</th>
                      <th className="text-end">{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.impressions" })}</th>
                      <th className="text-end">{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.clicks" })}</th>
                      <th className="text-end">{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.likes" })}</th>
                      <th className="text-end">{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.engagementRate" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.postsTable.map((post) => (
                      <tr key={post.id}>
                        <td className="li-analytics-table__title">{post.title}</td>
                        <td>{post.dateLabel}</td>
                        <td className="text-end">{formatNum(post.impressions)}</td>
                        <td className="text-end">{formatNum(post.clicks)}</td>
                        <td className="text-end">{formatNum(post.likes)}</td>
                        <td className="text-end">{formatNum(post.engagementRate, 2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </>
      ) : null}

      {tab === "demographics" ? (
        <div className="li-analytics-demo-grid">
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.demo.countries" })}</h3>
            <DemographicList
              rows={snapshot.demographics.countries}
              emptyKey="app.linkedinBusinessAnalytics.demo.empty"
            />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.demo.industries" })}</h3>
            <DemographicList
              rows={snapshot.demographics.industries}
              emptyKey="app.linkedinBusinessAnalytics.demo.empty"
            />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.demo.seniority" })}</h3>
            <DemographicList
              rows={snapshot.demographics.seniorities}
              emptyKey="app.linkedinBusinessAnalytics.demo.empty"
            />
          </div>
          <div className="li-analytics-card">
            <h3>{intl.formatMessage({ id: "app.linkedinBusinessAnalytics.demo.functions" })}</h3>
            <DemographicList
              rows={snapshot.demographics.functions}
              emptyKey="app.linkedinBusinessAnalytics.demo.empty"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
